import React, { ReactNode, useEffect, useId, useRef } from 'react';
import { FieldError } from 'react-hook-form';

import { cn } from 'support/helpers/cn/cn';
import { useMergeRefs } from 'support/helpers/hooks/useMergedRefs';
import { WithRef } from 'support/types/withRef';

type CheckboxType = {
  label?: string | ReactNode;
  description?: ReactNode;
  id?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  indeterminate?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

const Checkbox = ({
  id,
  label,
  description,
  className,
  disabled,
  error,
  indeterminate,
  ref: forwardedRef,
  ...otherProps
}: WithRef<CheckboxType, HTMLInputElement>) => {
  const generatedId = useId();
  const internalRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergeRefs([internalRef, forwardedRef]);

  useEffect(() => {
    if (internalRef.current) {
      internalRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <div className={cn('relative flex items-start')}>
      <div className="flex h-5 items-center">
        <input
          ref={mergedRef}
          type="checkbox"
          id={id || generatedId}
          aria-describedby={`${label}-description`}
          className={cn(
            className ?? '',
            'size-4 rounded-sm focus:border-gray-300 focus:bg-gray-50 focus:ring-0 focus:ring-offset-0',
            disabled
              ? 'cursor-not-allowed border-gray-200 bg-gray-200 text-gray-500 checked:border-gray-200 checked:bg-gray-200 checked:text-gray-400 checked:hover:border-gray-200 checked:hover:bg-gray-200 checked:hover:text-gray-400'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 checked:hover:border-gray-500 checked:hover:bg-gray-500 checked:hover:text-gray-500 focus-visible:text-gray-700 focus-visible:ring-2 focus-visible:ring-procuros-green-500 focus-visible:ring-offset-2 focus-visible:checked:border-gray-700 focus-visible:checked:bg-gray-700',
            error ? 'border-red-300 checked:border-red-300' : undefined,
          )}
          disabled={disabled}
          {...otherProps}
        />
      </div>

      {label || description ? (
        <div className={cn('ml-3 text-sm', disabled ? 'opacity-40' : undefined)}>
          {label ? (
            <label htmlFor={id || generatedId} className="font-medium text-gray-700">
              {label}
            </label>
          ) : null}

          {!!description && (
            <p id={`${label}-description`} className="text-gray-500">
              {description}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export { Checkbox };

export const CheckboxInABox = ({
  errors,
  description,
  ref,
  ...props
}: WithRef<CheckboxType & { description: ReactNode; errors?: FieldError }, HTMLInputElement | null>) => {
  return (
    <div>
      <div className={cn('flex rounded-lg p-4', errors || props.error ? 'bg-red-50' : 'bg-gray-50')}>
        <Checkbox {...props} description={props.label ? description : undefined} error={Boolean(errors)} ref={ref} />
        {description && !props.label ? <p className="ml-3 text-sm text-gray-700">{description}</p> : null}
      </div>
      {!!errors && <p className="mt-2 text-sm text-red-600">{errors.message}</p>}
    </div>
  );
};
